import {
  computeTenantEnabledModules,
  resolveEffectiveBillingPlan,
  type ModuleOrgType,
  type TenantModuleActivationSummary,
} from '../billing';
import type { HelpTenantFilterContext } from './index';
import type { HelpProgramTag } from './types';
import { isComplianceTrialAccessScopeTenant } from '../compliance-trial-access';
import type { ProviderType } from '../navigation/nav-v2';

type TenantConfigRow = {
  brandConfig?: Record<string, unknown> | null;
  selectedPackages?: string[] | null;
  billingPlan?: string | null;
  subscriptionStatus?: string | null;
  subscriptionTier?: string | null;
  moduleActivations?: TenantModuleActivationSummary[];
  isDemo?: boolean | null;
};

export function resolveHelpOrgTypeForProgram(providerType: ProviderType): ModuleOrgType | null {
  if (providerType === 'SAH') return 'HOME_CARE';
  if (providerType === 'RAC') return 'RESIDENTIAL';
  return null;
}

export function resolveHelpProgram(
  programFamily: string | null | undefined,
): HelpProgramTag | undefined {
  if (programFamily === 'SAH' || programFamily === 'RAC' || programFamily === 'CHSP') {
    return programFamily;
  }
  return undefined;
}

export function resolveHelpTenantContext(
  tenantId: string | null | undefined,
  config: TenantConfigRow | null | undefined,
): {
  enabledModules: string[] | null;
  orgType: ModuleOrgType | null;
  tenantContext: HelpTenantFilterContext;
} {
  const brandConfig = config?.brandConfig ?? null;
  const orgType = (brandConfig?.orgType as ModuleOrgType | undefined) ?? null;

  const tenantContext: HelpTenantFilterContext = {
    tenantId: tenantId ?? null,
    isDemo: config?.isDemo === true,
    orgType,
    providerType: 'UNKNOWN',
  };

  if (!config) {
    return { enabledModules: null, orgType: null, tenantContext };
  }

  if (tenantContext.isDemo) {
    return { enabledModules: null, orgType, tenantContext };
  }

  const selectedPackages =
    config.selectedPackages ?? (brandConfig?.selectedPackages as string[] | undefined) ?? [];

  const enabledModules = computeTenantEnabledModules({
    billingPlan: resolveEffectiveBillingPlan({
      billingPlan: config.billingPlan ?? null,
      subscriptionTier: config.subscriptionTier ?? null,
      legacyPlan: (brandConfig?.plan as string | null) ?? null,
    }),
    complianceTrial: isComplianceTrialAccessScopeTenant({
      billingPlan: config.billingPlan,
      subscriptionStatus: config.subscriptionStatus,
      isDemo: config.isDemo,
      selectedPackages,
      brandConfig,
    }),
    selectedPackages,
    orgType,
    systemEnabledModules: (brandConfig?.systemEnabledModules as string[]) ?? [],
    adminDisabledModules: (brandConfig?.adminDisabledModules as string[]) ?? [],
    legacyEnabledModules: (brandConfig?.enabledModules as string[]) ?? null,
    moduleActivations: config.moduleActivations ?? [],
    goLiveScope: brandConfig?.visibleGoLiveScope,
  });

  return { enabledModules, orgType, tenantContext };
}
